import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const UnlockList = () => {
  const [unlocks, setUnlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchDebounceRef = useRef(null);
  const [dateRange, setDateRange] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [viewModal, setViewModal] = useState(false);
  const [selectedUnlock, setSelectedUnlock] = useState(null);

  const fetchUnlocks = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        draw: 1,
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        date: dateRange
      };

      const response = await apiService.post('buyLeadList', params);
      if (response && response.data) {
        setUnlocks(response.data);
        setTotalRecords(response.recordsFiltered || response.recordsTotal || 0);
      }
    } catch (error) {
      toast.error('Failed to load unlock list');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, recordsPerPage, keyword, dateRange]);

  useEffect(() => {
    fetchUnlocks();
  }, [fetchUnlocks]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const formatDateTimeObject = (dateString) => {
    if (!dateString) return { date: '--', time: '--' };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }),
      time: date.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata' })
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? '--' : date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const formatBool = (value) => (value ? 'Yes' : 'No');

  const getAiResultBadge = (airesult) => {
    const intent = airesult?.intent || 'LOW';
    const config = {
      HIGH: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
      MEDIUM: 'bg-amber-50 text-amber-700 border border-amber-100',
      LOW: 'bg-slate-100 text-slate-700 border border-slate-200'
    };

    return {
      label: intent,
      color: config[intent] || config.LOW
    };
  };

  const formatAiScore = (airesult) => {
    if (airesult?.finalScore !== undefined && airesult?.finalScore !== null) return airesult.finalScore;
    if (airesult?.score !== undefined && airesult?.score !== null) return airesult.score;
    return '--';
  };

  const openViewModal = (unlock) => {
    setSelectedUnlock(unlock);
    setViewModal(true);
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  const selectedQuery = selectedUnlock?.queryId || {};
  const selectedVendor = selectedUnlock?.userId || {};
  const selectedUser = selectedQuery?.userId || {};

  return (
    <div className="p-0">
      <nav className="flex flex-wrap items-center text-xs sm:text-sm mb-3">
        <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-900 font-medium">Unlock List</span>
      </nav>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchInput(val);
                  clearTimeout(searchDebounceRef.current);
                  searchDebounceRef.current = setTimeout(() => {
                    setKeyword(val);
                    setCurrentPage(1);
                  }, 500);
                }}
                placeholder="Type Name, Phone or Email"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <input
              type="text"
              value={dateRange}
              onChange={(e) => { setDateRange(e.target.value); setCurrentPage(1); }}
              placeholder="Pick date range"
              className="w-full sm:w-full sm:w-[200px] px-2 sm:px-4 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : unlocks.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-unlock text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Unlocks Found</h3>
              <p className="text-gray-500">No unlocks match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500 text-xs uppercase">
                    <th className="px-2 sm:px-4 py-3">#</th>
                    <th className="px-2 sm:px-4 py-3">Vendor Detail</th>
                    <th className="px-2 sm:px-4 py-3">Query Detail</th>
                    <th className="px-2 sm:px-4 py-3 min-w-[160px]">Trip Info</th>
                    <th className="px-2 sm:px-4 py-3">User Detail</th>
                    <th className="px-2 sm:px-4 py-3 text-center">Amount</th>
                    <th className="px-2 sm:px-4 py-3 min-w-[220px]">Query / Unlock Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {unlocks.map((unlock, index) => {
                    const vendor = unlock.userId || {};
                    const query = unlock.queryId || {};
                    const user = query.userId || {};
                    const sourceLocation = query.leavingFrom?.name || 'N/A';
                    const destination = query.goingTo?.name || 'N/A';
                    const querySource = (query.source || 'other').toString().toLowerCase();
                    const sourceBadge = querySource === 'google' ? 'G' : (querySource === 'meta' ? 'M' : 'O');
                    const aiBadge = getAiResultBadge(query.airesult);

                    return (
                      <tr key={unlock._id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openViewModal(unlock)}>
                        <td className="px-2 sm:px-4 py-3">{(currentPage - 1) * recordsPerPage + index + 1}</td>
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col text-xs">
                            <span className="font-semibold" style={{ color: '#009ef7' }}>{vendor.first_name} {vendor.last_name}</span>
                            <Link
                              to={`/vendorWallet/${vendor._id}`}
                              style={{ color: '#009ef7' }}
                              className="no-underline hover:opacity-80"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {vendor.business_name ? vendor.business_name.charAt(0).toUpperCase() + vendor.business_name.slice(1) : ''}
                            </Link>
                            <span className="text-gray-500">{vendor.email || ''}</span>
                            <span className="text-gray-500">{vendor.phone || ''}</span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          {query._id ? (
                            <div className="min-w-[240px] max-w-[320px] text-xs text-slate-700">
                              <div className="flex items-center justify-between gap-3">
                                <a
                                  href={`https://www.travellinglark.com/travel-lead/details/${query.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#009ef7' }}
                                  className="no-underline hover:opacity-80 font-semibold text-sm leading-5"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Query #{query.id}
                                </a>
                                <div className="flex items-center gap-2">
                                  <span
                                    className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-[#009ef7]/10 px-2 text-[10px] font-semibold text-[#009ef7]"
                                    title={`Source: ${querySource}`}
                                  >
                                    {sourceBadge}
                                  </span>
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.04em] ${aiBadge.color}`}
                                    title={`Lead priority: ${aiBadge.label}`}
                                  >
                                    {aiBadge.label}
                                  </span>
                                </div>
                              </div>

                              <div className="mt-2 rounded-xl border border-slate-200 bg-white px-2.5 py-2">
                                <div className="flex items-start gap-2 text-[11px]">
                                  <div className="flex flex-col items-center pt-0.5">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                                    <span className="h-4 w-px bg-slate-200"></span>
                                    <span className="h-2 w-2 rounded-full bg-rose-500"></span>
                                  </div>
                                  <div className="min-w-0 flex-1 space-y-1.5">
                                    <div className="flex items-start gap-1.5">
                                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">From</span>
                                      <span className="break-words font-medium text-slate-700">{sourceLocation}</span>
                                    </div>
                                    <div className="flex items-start gap-1.5">
                                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-[0.06em] text-slate-400">To</span>
                                      <span className="break-words font-medium text-slate-700">{destination}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px]">
                                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-semibold text-slate-600">
                                  Score: {formatAiScore(query.airesult)}
                                </span>
                                <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-semibold capitalize text-slate-600">
                                  Call: {query.callStatus || 'pending'}
                                </span>
                              </div>
                            </div>
                          ) : '--'}
                        </td>
                        <td className="px-2 sm:px-4 py-3 min-w-[160px]">
                          <div className="flex flex-col gap-1 text-[11px] text-slate-700">
                            <div><span className="font-semibold text-slate-500">Date:</span> {formatDate(query.travelDate)}</div>
                            <div><span className="font-semibold text-slate-500">Pax:</span> {query.adult ?? '--'}A / {query.child ?? 0}C</div>
                            <div><span className="font-semibold text-slate-500">Duration:</span> {query.duration || '--'}</div>
                            <div><span className="font-semibold text-slate-500">Budget:</span> {query.budget ? `₹${query.budget}` : '--'}</div>
                            <div><span className="font-semibold text-slate-500">Hotel:</span> {query.hotelCategory?.length ? query.hotelCategory.join(', ') : '--'}</div>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3">
                          <div className="flex flex-col text-xs">
                            <span className="font-semibold" style={{ color: '#009ef7' }}>{user.first_name || ''} {user.last_name || ''}</span>
                            <span className="text-gray-500">
                              {user.email || ''} {user.emailVerified ? <i className="bi bi-check-circle-fill text-green-600 ml-1" title="Email Verified"></i> : <i className="bi bi-x-circle-fill text-red-600 ml-1" title="Email Not Verified"></i>}
                            </span>
                            <span className="text-gray-500">
                              {user.phone || ''} {user.phoneVerified ? <i className="bi bi-check-circle-fill text-green-600 ml-1" title="Phone Verified"></i> : <i className="bi bi-x-circle-fill text-red-600 ml-1" title="Phone Not Verified"></i>}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 sm:px-4 py-3 text-center">{unlock.amount}</td>
                        <td className="px-2 sm:px-4 py-3 text-xs text-gray-600 min-w-[220px]">
                          <div className="flex flex-col gap-1">
                            <span><span className="font-semibold text-gray-500">Query:</span> {query.createdAt ? formatDateTime(query.createdAt) : '-'}</span>
                            <span><span className="font-semibold text-gray-500">Unlock:</span> {formatDateTime(unlock.createdAt)}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && unlocks.length > 0 && (
          <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-xs sm:text-sm text-gray-700">Show</span>
              <select value={recordsPerPage} onChange={(e) => { setRecordsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm">
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-xs sm:text-sm text-gray-700">entries</span>
            </div>
            <div className="flex items-center gap-1 flex-wrap justify-center">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >
                «
              </button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  const isActive = currentPage === page;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className="px-3 py-1 border rounded text-xs sm:text-sm"
                      style={{
                        backgroundColor: isActive ? '#009ef7' : '#fff',
                        borderColor: isActive ? '#009ef7' : '#d1d5db',
                        color: isActive ? '#fff' : '#374151'
                      }}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2 text-gray-500">...</span>;
                }
                return null;
              })}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {viewModal && selectedUnlock && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px] p-2 sm:p-5">
          <div className="mx-auto max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl flex flex-col font-sans">
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur px-4 sm:px-6 py-2.5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-slate-900 mt-0.5">Lead Overview</h3>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center rounded-full bg-sky-50 text-sky-700 border border-sky-100 px-2.5 py-1 text-xs font-semibold">
                      <i className="bi bi-hash mr-1"></i>{selectedQuery.id || '--'}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 text-xs font-semibold">
                      Source: {selectedQuery.source || '--'}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-orange-50 text-orange-700 border border-orange-100 px-2.5 py-1 text-xs font-semibold">
                      Unlock Amount: {selectedUnlock.amount || '--'}
                    </span>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${getAiResultBadge(selectedQuery.airesult).color}`}>
                      {getAiResultBadge(selectedQuery.airesult).label}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2.5 py-1 text-xs font-semibold capitalize">
                      Call: {selectedQuery.callStatus || 'pending'}
                    </span>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <div className="text-right">
                    <p className="text-xs sm:text-sm text-slate-900 font-semibold">{formatDateTimeObject(selectedQuery.createdAt).date}</p>
                    <p className="text-[11px] text-slate-500">{formatDateTimeObject(selectedQuery.createdAt).time}</p>
                  </div>
                  <button
                    onClick={() => setViewModal(false)}
                    className="h-8 w-8 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center justify-center"
                    aria-label="Close"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto p-4 sm:p-5 bg-gradient-to-b from-slate-50 to-white">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2 rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <p className="text-xs uppercase text-slate-600 font-semibold mb-0">Contact Summary</p>
                      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Compact</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-1.5 text-[11px] leading-5">
                      <div className="flex items-start gap-2">
                        <span className="min-w-[92px] font-semibold text-blue-700">Traveler</span>
                        <span className="text-slate-900">{selectedUser.first_name || '--'} {selectedUser.last_name || ''}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="min-w-[92px] font-semibold text-emerald-700">Vendor</span>
                        <span className="text-slate-900">{selectedVendor.business_name || '--'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="min-w-[92px] font-semibold text-slate-600">Email</span>
                        <span className="text-slate-700 break-all">{selectedUser.email || '--'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="min-w-[92px] font-semibold text-slate-600">Person</span>
                        <span className="text-slate-700">{selectedVendor.first_name || '--'} {selectedVendor.last_name || ''}</span>
                      </div>
                      <div className="flex items-start gap-2 md:col-span-2">
                        <span className="min-w-[92px] font-semibold text-slate-600">Phone</span>
                        <span className="text-slate-700">{selectedUser.phone || '--'}</span>
                      </div>
                      <div className="flex items-start gap-2 md:col-start-2">
                        <span className="min-w-[92px] font-semibold text-slate-600">Vendor Email</span>
                        <span className="text-slate-700 break-all">{selectedVendor.email || '--'}</span>
                      </div>
                      <div className="flex items-start gap-2 md:col-start-2">
                        <span className="min-w-[92px] font-semibold text-slate-600">Vendor Phone</span>
                        <span className="text-slate-700">{selectedVendor.phone || '--'}</span>
                      </div>

                    </div>
                  </div>

                  <div className="rounded-xl border border-orange-100 bg-orange-50/70 p-3.5">
                    <p className="text-xs uppercase text-orange-700 font-semibold mb-2">Trip Route</p>
                    <p className="text-slate-800 text-sm"><span className="font-semibold">Travel Date:</span> {formatDate(selectedQuery.travelDate)}</p>
                    <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">From:</span> {selectedQuery.leavingFrom?.name || '--'}</p>
                    <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">To:</span> {selectedQuery.goingTo?.name || '--'}</p>
                  </div>

                  <div className="rounded-xl border border-sky-100 bg-sky-50/70 p-3.5">
                    <p className="text-xs uppercase text-sky-700 font-semibold mb-2">Lead Timeline</p>
                    <p className="text-slate-800 text-sm"><span className="font-semibold">Query Date:</span> {formatDateTime(selectedQuery.createdAt)}</p>
                    <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">Unlock Date:</span> {formatDateTime(selectedUnlock.createdAt)}</p>
                    <p className="text-slate-800 text-sm mt-1"><span className="font-semibold">Amount:</span> {selectedUnlock.amount || '--'}</p>
                  </div>

                  <div className="sm:col-span-2 rounded-2xl border border-slate-200 bg-white p-3 sm:p-4">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">Other Details</p>
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-[11px] text-slate-600 font-semibold">Compact</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-2">
                      {[
                        { label: 'Adults / Children', value: `${selectedQuery.adult ?? '--'} / ${selectedQuery.child ?? '--'}` },
                        { label: 'Duration', value: selectedQuery.duration || '--' },
                        { label: 'Total Budget', value: (selectedQuery.budget != null && selectedQuery.adult != null) ? `${selectedQuery.budget * selectedQuery.adult} ( ${selectedQuery.budget || 0} * ${selectedQuery.adult || 0} )` : '--' },
                        { label: 'Tour Type', value: selectedQuery.tourType || '--' },
                        { label: 'Preferred Call', value: selectedQuery.preferredCallTime || '--' },
                        { label: 'Will Book', value: selectedQuery.willBook || '--' },
                        { label: 'Hotel Category', value: selectedQuery.hotelCategory?.length ? selectedQuery.hotelCategory.join(', ') : '--' },
                        { label: 'Contacted Agent', value: selectedQuery.contactedAgent || '--' },
                        { label: 'Dates Flexible', value: selectedQuery.datesFlexible || '--' },
                        { label: 'Most Important', value: selectedQuery.mostImportant || '--' },
                        { label: 'Ticket / Reward', value: `${formatBool(selectedQuery.haveTicket)} / ${formatBool(selectedQuery.appliedForReward)}` }
                      ].map((item) => (
                        <div key={item.label} className="flex items-center text-[11px] leading-4 text-slate-800 min-h-[22px]">
                          <span className="min-w-[110px] text-slate-600 font-semibold">{item.label}:</span>
                          <span className="text-slate-900 truncate">{item.value}</span>
                        </div>
                      ))}
                    </div>
                    {selectedQuery.description ? (
                      <div className="mt-3 rounded-lg bg-slate-50 p-2">
                        <p className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Description</p>
                        <p className="text-xs leading-5 text-slate-700 min-h-[26px] whitespace-pre-wrap">{selectedQuery.description}</p>
                      </div>
                    ) : null}
                  </div>
                </div>

                {selectedQuery.closeReason && (
                  <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50/70 p-3.5">
                    <p className="text-xs uppercase text-rose-700 font-semibold mb-2">Close Reason</p>
                    <p className="text-sm leading-6 text-slate-700 whitespace-pre-wrap">{selectedQuery.closeReason}</p>
                  </div>
                )}

                <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/60 p-3.5">
                  <div className="flex flex-wrap items-center gap-2 mb-2.5">
                    <p className="text-xs uppercase text-violet-700 font-semibold mb-0">Lead Priority</p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.04em] ${getAiResultBadge(selectedQuery.airesult).color}`}>
                      {getAiResultBadge(selectedQuery.airesult).label}
                    </span>
                    <span className="text-xs text-slate-600">Score: {formatAiScore(selectedQuery.airesult)}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Urgency:</span> {selectedQuery.airesult?.urgency || '--'}</p>
                    <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Confidence:</span> {selectedQuery.airesult?.confidence ?? '--'}</p>
                    <p className="text-sm text-slate-700"><span className="font-semibold text-slate-900">Hot Lead:</span> {formatBool(selectedQuery.airesult?.isHotLead)}</p>
                    <p className="text-sm text-slate-700 sm:col-span-2"><span className="font-semibold text-slate-900">Reason:</span> {selectedQuery.airesult?.reason || '--'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 bg-white px-4 sm:px-6 py-3 flex justify-end">
              <button
                onClick={() => setViewModal(false)}
                className="px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-800 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnlockList;
